import React, { useEffect, useState } from "react";
import "./CartPage.css";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const fetchCart = async () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  
    if (!token) {
      setError("You are not logged in. Please log in to view your cart.");
      return;
    }
  
    try {
      const response = await fetch("https://backend.rubiajewels.com/api/cart", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
  
      if (response.ok) {
        const data = await response.json();
        setCartItems(data);
      } else if (response.status === 401) {
        console.log("Access token expired, refreshing...");
  
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          setError("Session expired. Please log in again.");
          return;
        }
  
        const refreshResponse = await fetch("https://backend.rubiajewels.com/api/refresh-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
  
        if (refreshResponse.ok) {
          const { accessToken } = await refreshResponse.json();
          localStorage.setItem("token", accessToken);
  
          return fetchCart();
        } else {
          setError("Session expired. Please log in again.");
        }
      } else {
        setError("Failed to fetch cart items.");
      }
    } catch (error) {
      setError("Error fetching cart items.");
      console.error(error);
    }
  };

  useEffect(() => {
    axios
      .get("https://backend.rubiajewels.com/api/cart", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        const itemsWithQuantity = response.data.map((item) => ({
          ...item,
          quantity: Number(item.quantity) || 1,
        }));
        setCartItems(itemsWithQuantity);
      })
      .catch((error) => console.error("Error fetching cart:", error));
  }, []);

  const redirectToProductPage = () => {
    navigate("/checkout");
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`https://backend.rubiajewels.com/api/cart/${itemId}`, {
        headers: {
          Authorization: `Bearer ${token}`, 
        },
      });
      setCartItems((prevItems) =>
        prevItems.filter((item) => item._id !== itemId)
      );
    } catch (error) {
      console.error("Error removing item from cart:", error);
      setError("Failed to remove item from cart.");
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    newQuantity = parseInt(newQuantity, 10);

    if (isNaN(newQuantity) || newQuantity < 1) {
      console.error("Invalid quantity:", newQuantity);
      setError("Invalid quantity.");
      return;
    }

    console.log("Updating quantity:", { itemId, newQuantity });

    try {
      const response = await axios.put(
        `https://backend.rubiajewels.com/api/cart/${itemId}`,
        { quantity: newQuantity },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );


      if (response.status === 200) {
        setCartItems((prevItems) =>
          prevItems.map((item) =>
            item._id === itemId ? { ...item, quantity: newQuantity } : item
          )
        );
      } else {
        throw new Error("Failed to update quantity");
      }
    } catch (error) {
      console.error("Error updating quantity:", error.response?.data || error);
      setError("Failed to update item quantity.");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    
    if (token) {
      fetchCart(); 
    } else {
      setError("You are not logged in. Please log in to view your cart.");
    }
  }, []);
  

  return (
    <div className="shopping-cart-container">
      <Link to="/" className="back-link">
        ← Back to Shopping
      </Link>
      <h1 className="cart-title">Shopping Cart</h1>

      {error && <p className="error-message">{error}</p>}

      {cartItems.length > 0 ? (
        cartItems.map((item) => (
          <div className="cart-item" key={item._id}>
            <img
              src={`https://backend.rubiajewels.com/${item.image.replace(/\\/g, "/")}`}
              alt={item.name}
              className="product-image"
            />
            <div className="product-details">
              <h2 className="product-title">{item.name}</h2>
              <div className="product-variant">
                <p>Selected Color : {item.color || "Not Selected"}</p>
                <p>Selected Size : {item.size || "Not Selected"}</p>
                <p>Selected Karat : {item.karat || "Not Selected"}</p>
                <p>Selected Stone : {item.stone || "Not Selected"}</p>
              </div>
              <div className="quantity-controls">
                <button
                  className="quantity-btn"
                  onClick={() => {
                    console.log(
                      "Decreasing Quantity: ",
                      item.quantity,
                      "New:",
                      item.quantity - 1
                    );
                    updateQuantity(item._id, item.quantity - 1);
                  }}
                  disabled={item.quantity <= 1}
                >
                  −
                </button>
                <span>{item.quantity}</span>
                <button
                  className="quantity-btn"
                  onClick={() => {
                    console.log(
                      "Increasing Quantity: ",
                      item.quantity,
                      "New:",
                      item.quantity + 1
                    );
                    updateQuantity(item._id, item.quantity + 1);
                  }}
                >
                  +
                </button>

                <button
                  className="remove-link"
                  onClick={() => removeItem(item._id)}
                >
                  Remove
                </button>
              </div>
            </div>
            <div className="product-pricing">
              <p className="original-price">${item.originalPrice}</p>
              <p className="discounted-price">${item.price}</p>
            </div>
          </div>
        ))
      ) : (
        <p>Your cart is empty</p>
      )}

      <div className="additional-section">
        <textarea
          className="gift-note"
          placeholder="Type your message here..."
        ></textarea>

        <div className="subtotal-section">
          <div className="total-items">
            <p className="subtotal-amount">Total</p>
            <p>
              $
              {cartItems
                .reduce((acc, item) => acc + item.price * item.quantity, 0)
                .toFixed(2)}
            </p>
          </div>
          <button className="checkout-btn" onClick={redirectToProductPage}>
            CHECK OUT
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
